import React, { useMemo } from 'react';

/**
 * AttendanceChart — horizontal timeline chart for a calendar month.
 *
 * X axis: business-day start (e.g. 06:00) → same time next day, a full 24h window.
 * Y axis: day 1 → last day of month.
 *
 * All time math is done in "axis minutes" = minutes elapsed since the business-day
 * start. This makes cross-midnight shifts monotonic (00:30 is LATER than 21:00 when
 * the day starts at 06:00), so late/early/break comparisons all just work.
 *
 * Colours:
 *   Green  — time at shop (in → out)
 *   Yellow — break between sessions
 *   Red    — late arrival (shop open → actual in) or early departure (actual out → shop close)
 */

const DAY_MINS = 24 * 60;

const timeToMins = (hhMm) => {
  if (!hhMm) return null;
  const [h, m] = String(hhMm).split(':').map(Number);
  return h * 60 + m;
};

export default function AttendanceChart({ data, calendarMonth }) {
  const punchRecords = data?.punch_records || [];
  const shopHoursPerDay = data?.shop_hours_per_day || {};
  const dailyStatus = data?.daily_status || {};
  const lateThreshold = data?.late_threshold_minutes ?? 15;
  const axisStartHour = data?.business_start_hour ?? 6;
  const axisStartMin = axisStartHour * 60;

  // Absolute minute-of-day → axis minutes (0..1440 elapsed since business start)
  const toAxis = (absMins) => (((absMins - axisStartMin) % DAY_MINS) + DAY_MINS) % DAY_MINS;
  const pct = (axisMins) => (axisMins / DAY_MINS) * 100;

  // 8 buckets of 3 hours each, for the scale header + gridlines
  const BUCKETS = useMemo(() => Array.from({ length: 8 }, (_, i) => {
    const hour = (axisStartHour + i * 3) % 24;
    return { hour, leftPct: (i / 8) * 100 };
  }), [axisStartHour]);

  const [year, month] = useMemo(() => {
    if (!calendarMonth) { const n = new Date(); return [n.getFullYear(), n.getMonth() + 1]; }
    const parts = String(calendarMonth).split('-').map(Number);
    return [parts[0], parts[1]];
  }, [calendarMonth]);

  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);

  // Group punches by business day, sorted by axis order (cross-midnight safe)
  const punchesByDate = useMemo(() => {
    const map = {};
    for (const p of punchRecords) {
      if (!map[p.date]) map[p.date] = [];
      map[p.date].push(p);
    }
    for (const d of Object.keys(map)) {
      map[d].sort((a, b) => toAxis(timeToMins(a.time)) - toAxis(timeToMins(b.time)));
    }
    return map;
  }, [punchRecords, axisStartHour]); // eslint-disable-line

  const fmtAxis = (axisMins) => {
    const abs = (axisMins + axisStartMin) % DAY_MINS;
    return `${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  };

  const rows = useMemo(() => {
    const result = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const ds = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const punches = punchesByDate[ds] || [];
      const shopHours = shopHoursPerDay[ds];
      const status = dailyStatus[ds];

      const segments = [];
      const shopOpenAxis = shopHours ? toAxis(timeToMins(shopHours.open)) : null;
      const shopCloseAxis = shopHours && shopHours.close ? toAxis(timeToMins(shopHours.close)) : null;

      if (punches.length > 0) {
        // Pair in/out punches into sessions (axis minutes)
        const sessions = [];
        let pendingIn = null;
        for (const p of punches) {
          const a = toAxis(timeToMins(p.time));
          if (p.punch_type === 'in') {
            pendingIn = a;
          } else if (p.punch_type === 'out' && pendingIn !== null) {
            sessions.push({ in: pendingIn, out: a });
            pendingIn = null;
          }
        }
        if (pendingIn !== null) sessions.push({ in: pendingIn, out: null });

        for (let si = 0; si < sessions.length; si++) {
          const { in: inA, out: outA } = sessions[si];
          if (outA !== null) {
            // Green time at shop
            segments.push({
              left: pct(inA), width: pct(Math.max(0, outA - inA)),
              color: 'bg-green-500',
              title: `In ${fmtAxis(inA)} → Out ${fmtAxis(outA)}`,
            });
            // Break to next session (yellow)
            if (si + 1 < sessions.length && sessions[si + 1].in > outA) {
              segments.push({
                left: pct(outA), width: pct(sessions[si + 1].in - outA),
                color: 'bg-yellow-400',
                title: `Break ${fmtAxis(outA)} → ${fmtAxis(sessions[si + 1].in)}`,
              });
            }
          } else {
            // Still in (no out punch yet)
            const nowAxis = toAxis(new Date().getHours() * 60 + new Date().getMinutes());
            const end = nowAxis > inA ? Math.min(nowAxis, inA + 12 * 60) : inA + 60;
            segments.push({
              left: pct(inA), width: pct(end - inA),
              color: 'bg-green-400 opacity-80',
              title: `In ${fmtAxis(inA)} (still in)`,
            });
          }
        }

        // Not in shop before first punch-in: shop open → first in. Shown red for ANY gap (they
        // weren't in the shop), regardless of whether it's large enough to be a "late" penalty.
        if (shopOpenAxis !== null && sessions.length > 0) {
          const firstIn = sessions[0].in;
          const lateMins = firstIn - shopOpenAxis;
          if (lateMins > 0.5) {
            segments.push({
              left: pct(shopOpenAxis), width: pct(lateMins),
              color: 'bg-red-500', zIndex: 2,
              title: lateMins > lateThreshold ? `Late by ${Math.round(lateMins)} min` : `Not in shop — arrived ${Math.round(lateMins)} min after open`,
            });
          }
        }

        // Not in shop after last punch-out: last out → shop close. Red for ANY early gap.
        if (shopCloseAxis !== null && sessions.length > 0) {
          const lastOut = sessions[sessions.length - 1].out;
          if (lastOut !== null) {
            const earlyMins = shopCloseAxis - lastOut;
            if (earlyMins > 0.5) {
              segments.push({
                left: pct(lastOut), width: pct(earlyMins),
                color: 'bg-red-400', zIndex: 2,
                title: earlyMins > 15 ? `Left ${Math.round(earlyMins)} min early` : `Not in shop — left ${Math.round(earlyMins)} min before close`,
              });
            }
          }
        }
      }

      result.push({ day, ds, segments, shopOpenAxis, shopCloseAxis, status, hasPunches: punches.length > 0 });
    }
    return result;
  }, [daysInMonth, year, month, punchesByDate, shopHoursPerDay, dailyStatus, lateThreshold, axisStartHour]); // eslint-disable-line

  const todayStr = new Date().toISOString().slice(0, 10);
  const LABEL_W = 'w-12'; // day-label column width (must match header spacer)

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 overflow-x-auto">
      <h3 className="text-sm font-semibold text-gray-700 mb-2">Attendance Timeline</h3>

      {/* Scale header — 8 three-hour buckets, aligned with the timeline bars */}
      <div className="flex items-end gap-1 mb-1">
        <div className={`${LABEL_W} flex-shrink-0`} />
        <div className="flex-1 relative h-4">
          {BUCKETS.map(({ hour, leftPct }) => (
            <div
              key={hour}
              className="absolute top-0 text-[9px] text-gray-400 font-mono -translate-x-1/2"
              style={{ left: `${leftPct}%` }}
            >
              {String(hour).padStart(2, '0')}
            </div>
          ))}
          {/* closing tick (wraps back to start) at the far right */}
          <div className="absolute top-0 right-0 text-[9px] text-gray-400 font-mono translate-x-1/2">
            {String(axisStartHour).padStart(2, '0')}
          </div>
        </div>
      </div>

      {/* Day rows */}
      <div className="space-y-0.5">
        {rows.map(({ day, ds, segments, shopOpenAxis, shopCloseAxis, status, hasPunches }) => {
          const isToday = ds === todayStr;
          const dayLabel = String(day).padStart(2, '0');
          const dow = ['Su','Mo','Tu','We','Th','Fr','Sa'][new Date(ds).getDay()];
          const isWeekend = dow === 'Su' || dow === 'Sa';

          let rowBg = 'bg-gray-50';
          if (!hasPunches && status === 'absent') rowBg = 'bg-red-50';
          else if (!hasPunches && status === 'leave') rowBg = 'bg-blue-50';
          else if (isToday) rowBg = 'bg-yellow-50';

          return (
            <div key={ds} className={`flex items-center gap-1 rounded ${rowBg} ${isToday ? 'ring-1 ring-yellow-400' : ''}`} style={{ height: 22 }}>
              <div className={`${LABEL_W} flex-shrink-0 text-right pr-1.5 text-[10px] font-mono select-none ${isWeekend ? 'text-blue-500 font-bold' : 'text-gray-500'}`}>
                {dow}{dayLabel}
              </div>

              <div className="flex-1 relative h-4 rounded overflow-hidden bg-gray-100">
                {/* 3-hour bucket gridlines (light) */}
                {BUCKETS.map(({ hour, leftPct }) => (
                  leftPct === 0 ? null : (
                    <div key={hour} className="absolute inset-y-0 w-px bg-gray-200" style={{ left: `${leftPct}%` }} />
                  )
                ))}

                {/* Business hours shading */}
                {shopOpenAxis !== null && (
                  <div
                    className="absolute inset-y-0 bg-gray-200/70"
                    style={{
                      left: `${pct(shopOpenAxis)}%`,
                      width: shopCloseAxis !== null
                        ? `${pct(((shopCloseAxis - shopOpenAxis) + DAY_MINS) % DAY_MINS)}%`
                        : '30%',
                    }}
                  />
                )}

                {/* Segments */}
                {segments.map((seg, i) => (
                  <div
                    key={i}
                    title={seg.title}
                    className={`absolute inset-y-0 ${seg.color} rounded-sm`}
                    style={{ left: `${seg.left}%`, width: `${Math.max(seg.width, 0.3)}%`, zIndex: seg.zIndex || 1, minWidth: 2 }}
                  />
                ))}

                {/* Vertical lines: business open / close */}
                {shopOpenAxis !== null && (
                  <div className="absolute inset-y-0 w-px bg-gray-600/70 z-10" style={{ left: `${pct(shopOpenAxis)}%` }} />
                )}
                {shopCloseAxis !== null && (
                  <div className="absolute inset-y-0 w-px bg-gray-600/50 z-10" style={{ left: `${pct(shopCloseAxis)}%` }} />
                )}

                {/* Absent/Leave label */}
                {!hasPunches && status && (
                  <div className="absolute inset-0 flex items-center px-1">
                    <span className={`text-[8px] font-semibold uppercase ${
                      status === 'absent' ? 'text-red-500' : status === 'leave' ? 'text-blue-500' : 'text-gray-400'
                    }`}>{status.replace('_', ' ')}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100">
        {[
          { color: 'bg-green-500', label: 'At shop' },
          { color: 'bg-yellow-400', label: 'Break' },
          { color: 'bg-red-500', label: 'Late / Early departure' },
          { color: 'bg-gray-200', label: 'Business hours' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${color}`} />
            <span className="text-[10px] text-gray-500">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-px h-3 bg-gray-600" />
          <span className="text-[10px] text-gray-500">Business open / close</span>
        </div>
        <span className="text-[10px] text-gray-400 ml-auto">Scale: 3-hour buckets from {String(axisStartHour).padStart(2,'0')}:00</span>
      </div>
    </div>
  );
}
